import type { AppDispatch } from "../../services/redux/app/store";
import { mergeMenuLookupsFromItems } from "../../services/redux/slices/configSlice";
import { fetchData } from "../APICalls/buildCalls";
import LS from "../LocalStorage";
import { LocalStorageEnum } from "../enums";
import env from "../env.config";
import { ItemType, SubItemType } from "../types";

/**
 * Re-fetch the items a checkout just rejected as outdated, so the guest can
 * re-add them at the CURRENT version.
 *
 * The create-receipt body takes each line's version from the persisted
 * `config.subItemLookupByUuid`, and that lookup only ever moves UP
 * (mergeSubItemRecords). Rejecting with ReqItemsContainsOutDatedItems drops
 * the lines from the cart but leaves the lookup where it was, so a guest who
 * re-adds from a menu loaded before a re-sync sends the same stale version and
 * is rejected again -- indefinitely, across reloads, because the lookup and
 * ITEMS_DATA both live in local storage. Dippy's, 2026-09-25: one guest sent
 * 2335/2340 seven times over two hours while the server and the item pages
 * were on 2340/2345.
 *
 * Fetches the full item (not just the version from the 210 body) so prices and
 * addon groups are refreshed with it: raising the version alone would get the
 * next checkout past the check with data the check exists to reject.
 *
 * Best effort: a failed fetch drops that item's ITEMS_DATA entry so its page
 * falls back to the freshly built static props instead of the stale copy.
 */
export const refreshOutdatedItems = (
  dispatch: AppDispatch,
  outdatedSubItems: SubItemType[] | undefined
): Promise<void> => {
  const itemsToRefresh = new Map<number, number | string | undefined>();
  for (const subItem of outdatedSubItems ?? []) {
    const itemId = subItem.item?.id ?? subItem.itemId;
    if (itemId !== undefined && itemId !== null) {
      itemsToRefresh.set(itemId, subItem.item?.restaurantId);
    }
  }

  return refreshItemsById(dispatch, itemsToRefresh);
};

/**
 * The same refresh keyed by item id, for callers that hold ids rather than the
 * sub-items of a rejection body (the MENU_VERSION_CHANGE socket, the item page).
 *
 * `/items/item/<id>` is the only endpoint that carries the full addon tree --
 * `/category/details` omits it -- so this is what corrects addon groups and
 * option row ids, not just the version.
 */
export const refreshItemsById = async (
  dispatch: AppDispatch,
  itemsToRefresh: Map<number, number | string | undefined>
): Promise<void> => {
  if (itemsToRefresh.size === 0) {
    return;
  }

  const freshItems: ItemType[] = [];
  const staleItemIds: number[] = [];

  await Promise.all(
    Array.from(itemsToRefresh.entries()).map(async ([itemId, restaurantId]) => {
      try {
        // itemVersion=-1 never matches, so the endpoint always answers with
        // the full current item rather than a 304.
        const res = await fetchData(
          `/items/item/${itemId}?itemVersion=-1&restaurantId=${
            restaurantId ?? env.restaurantId
          }`,
          env.apiV2
        );
        if (res.status !== 200) {
          staleItemIds.push(itemId);

          return;
        }
        const item: ItemType & { error?: string } = await res.json();
        if (!item || item.error || item.id !== itemId) {
          staleItemIds.push(itemId);

          return;
        }
        freshItems.push(item);
      } catch (error) {
        console.error("Failed to refresh outdated item", itemId, error);
        staleItemIds.push(itemId);
      }
    })
  );

  const itemsLS: { [key: number]: ItemType } =
    LS._get(LocalStorageEnum.ITEMS_DATA, true) || {};
  freshItems.forEach((item) => {
    itemsLS[item.id] = item;
  });
  staleItemIds.forEach((itemId) => {
    delete itemsLS[itemId];
  });
  LS._set(LocalStorageEnum.ITEMS_DATA, itemsLS, true);

  if (freshItems.length > 0) {
    dispatch(mergeMenuLookupsFromItems(freshItems));
  }
};
